/**
 * Refcounted single-subscription lifecycle shared across "live surface"
 * managers (sidebar recents, computer dashboard). It owns one underlying
 * subscription for any number of retainers, optionally keeps it open across an
 * idle window after the last release, re-opens with bounded backoff after a
 * terminal transport error, and optionally parks long-hidden tabs until they
 * become visible again. Surface-specific payload handling — a Relay store
 * `updater`, a listener fan-out, etc. — lives entirely in the `open` callback,
 * so each manager is just that callback plus its own consumer bookkeeping.
 */
const DEFAULT_RESUBSCRIBE_BASE_MS = 5_000;
const DEFAULT_RESUBSCRIBE_MAX_MS = 60_000;
const DEFAULT_MAX_CONSECUTIVE_FAILURES = 10;
function isBrowserOffline() {
    return typeof navigator !== 'undefined' && navigator.onLine === false;
}
function isTabHidden() {
    return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}
function canListenForRecovery() {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
}
export function createRetainedSubscription(config) {
    const baseMs = config.resubscribeBaseMs ?? DEFAULT_RESUBSCRIBE_BASE_MS;
    const maxMs = config.resubscribeMaxMs ?? DEFAULT_RESUBSCRIBE_MAX_MS;
    const maxConsecutiveFailures = config.maxConsecutiveFailures ?? DEFAULT_MAX_CONSECUTIVE_FAILURES;
    let refs = 0;
    let disposable = null;
    let idleTimer = null;
    let retryTimer = null;
    let hiddenParkTimer = null;
    let watchingVisibility = false;
    let retryAttempts = 0;
    let parked = false;
    let parkedWithOnlineResume = false;
    // Bumped by reset() so release handles and in-flight callbacks captured
    // before the reset become no-ops and can't underflow refs or revive a
    // torn-down subscription.
    let generation = 0;
    function open() {
        if (disposable !== null || retryTimer !== null) {
            return;
        }
        // Guard both signal callbacks on the open-time generation, like release
        // handles: a terminal/active callback the transport delivers after a
        // reset() must not clobber a fresh subscription's disposer or schedule a
        // duplicate resubscribe.
        const openedGeneration = generation;
        let terminated = false;
        const opened = config.open({
            onActive: () => {
                if (openedGeneration !== generation || terminated) {
                    return;
                }
                retryAttempts = 0;
            },
            onTerminated: () => {
                if (openedGeneration !== generation || terminated) {
                    return;
                }
                terminated = true;
                const close = disposable;
                disposable = null;
                close?.();
                scheduleResubscribe();
            },
        });
        // If the subscription terminated synchronously during open(), the returned
        // disposer is for a dead subscription; storing it would block the scheduled
        // resubscribe (open() bails while disposable is non-null).
        if (terminated) {
            opened();
        }
        else {
            disposable = opened;
        }
    }
    function scheduleResubscribe() {
        if (refs <= 0 || retryTimer !== null) {
            return;
        }
        // Offline attempts are guaranteed to fail, and a long failure streak means
        // the target is effectively dead for this client. Park until a recovery
        // signal — but only in hidden tabs: a visible tab has no reliable resume
        // signal (no visibilitychange, and `online` never fires if the browser
        // misreported offline or was online throughout an outage), so it keeps
        // retrying at the bounded max backoff instead of freezing until a tab
        // switch.
        if (isTabHidden() &&
            (isBrowserOffline() || retryAttempts >= maxConsecutiveFailures) &&
            canListenForRecovery()) {
            park({ resumeOnOnline: true });
            return;
        }
        const backoff = Math.min(baseMs * 2 ** retryAttempts, maxMs);
        // 50-100% of the backoff, so a fleet-wide disconnect (e.g. a server
        // deploy closing every websocket) does not resubscribe in lockstep.
        const delay = backoff * (0.5 + Math.random() * 0.5);
        retryAttempts += 1;
        retryTimer = setTimeout(() => {
            retryTimer = null;
            if (refs > 0) {
                open();
            }
        }, delay);
    }
    function handleVisibilityRecovery() {
        if (document.visibilityState === 'visible') {
            resumeFromPark();
        }
    }
    // resumeOnOnline distinguishes the two park types: a failure/offline park
    // wants to retry as soon as connectivity returns, but a hidden-tab park of
    // a HEALTHY subscription must ignore `online` — an online blip in a
    // still-hidden tab would revive the subscription with nothing left to
    // re-arm the hidden-park timer, leaving it live in the background
    // indefinitely. Hidden parks resume only on visibility or a new retain.
    // While hidden-parked, both this visibilitychange listener and the
    // persistent watchVisibility one are registered; on becoming visible one
    // resumes and the other cancels the (idle) hidden timer — both idempotent.
    function park({ resumeOnOnline }) {
        if (parked) {
            return;
        }
        parked = true;
        parkedWithOnlineResume = resumeOnOnline;
        cancelHiddenPark();
        if (resumeOnOnline) {
            window.addEventListener('online', resumeFromPark);
        }
        document.addEventListener('visibilitychange', handleVisibilityRecovery);
    }
    function cancelHiddenPark() {
        if (hiddenParkTimer !== null) {
            clearTimeout(hiddenParkTimer);
            hiddenParkTimer = null;
        }
    }
    // Hidden tabs dominate live-subscription concurrency, so a tab hidden for
    // longer than a tab-switch flap parks its healthy subscription: the
    // transport is dropped and the park machinery re-opens on the next
    // visibilitychange to visible. Unlike the failure park above, this one
    // tears down a live subscription rather than just stopping a retry loop.
    function scheduleHiddenPark() {
        if (hiddenParkTimer !== null || parked || refs <= 0) {
            return;
        }
        const parkAfterMs = config.hiddenParkAfterMs?.() ?? 0;
        if (parkAfterMs <= 0 || !canListenForRecovery()) {
            return;
        }
        hiddenParkTimer = setTimeout(() => {
            hiddenParkTimer = null;
            if (refs <= 0 || parked || !isTabHidden()) {
                return;
            }
            if (retryTimer !== null) {
                clearTimeout(retryTimer);
                retryTimer = null;
            }
            retryAttempts = 0;
            const current = disposable;
            disposable = null;
            current?.();
            park({ resumeOnOnline: false });
        }, parkAfterMs);
    }
    function handleVisibilityChange() {
        if (document.visibilityState === 'hidden') {
            scheduleHiddenPark();
        }
        else {
            cancelHiddenPark();
        }
    }
    function watchVisibility() {
        if (watchingVisibility || !canListenForRecovery()) {
            return;
        }
        watchingVisibility = true;
        document.addEventListener('visibilitychange', handleVisibilityChange);
    }
    function unwatchVisibility() {
        if (!watchingVisibility) {
            return;
        }
        watchingVisibility = false;
        document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
    function unpark() {
        if (!parked) {
            return;
        }
        parked = false;
        if (parkedWithOnlineResume) {
            window.removeEventListener('online', resumeFromPark);
            parkedWithOnlineResume = false;
        }
        document.removeEventListener('visibilitychange', handleVisibilityRecovery);
    }
    // A subscription can stay parked with zero consumers: releasing the last
    // ref inside an idle-dispose window defers dispose() (and its unpark) to
    // the idle timer. A recovery signal in that window must not reopen a
    // consumerless subscription — unpark and let retain()/dispose() settle it.
    function resumeFromPark() {
        if (!parked) {
            return;
        }
        unpark();
        if (refs <= 0) {
            return;
        }
        retryAttempts = 0;
        open();
        if (isTabHidden()) {
            // A failure park can resume via `online` while the tab is still hidden,
            // and no visibilitychange will fire on an already-hidden tab — re-arm
            // the hidden park here or the revived subscription outlives the hidden
            // window it should park under.
            scheduleHiddenPark();
        }
    }
    function clearTimers() {
        if (idleTimer !== null) {
            clearTimeout(idleTimer);
            idleTimer = null;
        }
        if (retryTimer !== null) {
            clearTimeout(retryTimer);
            retryTimer = null;
        }
    }
    function dispose() {
        clearTimers();
        cancelHiddenPark();
        unwatchVisibility();
        unpark();
        retryAttempts = 0;
        const current = disposable;
        disposable = null;
        current?.();
    }
    function retain() {
        refs += 1;
        if (idleTimer !== null) {
            clearTimeout(idleTimer);
            idleTimer = null;
        }
        if (parked) {
            // A new consumer is a fresh recovery signal (e.g. user navigated back).
            resumeFromPark();
        }
        else {
            open();
        }
        watchVisibility();
        if (isTabHidden()) {
            // Retained while already hidden (a background-opened tab): arm the
            // hidden park directly since no visibilitychange will fire.
            scheduleHiddenPark();
        }
        const retainedGeneration = generation;
        let released = false;
        return () => {
            // Ignore once-released handles and any handle from before a reset(), so a
            // stale release can't underflow refs or dispose a fresh retain.
            if (released || retainedGeneration !== generation) {
                return;
            }
            released = true;
            refs -= 1;
            if (refs > 0) {
                return;
            }
            const idleMs = config.getIdleDisposeMs?.() ?? 0;
            if (idleMs > 0) {
                idleTimer = setTimeout(() => {
                    if (refs <= 0) {
                        dispose();
                    }
                }, idleMs);
            }
            else {
                dispose();
            }
        };
    }
    function reset() {
        generation += 1;
        refs = 0;
        dispose();
    }
    return { retain, reset };
}
//# sourceMappingURL=createRetainedSubscription.js.map